import type { RowData } from "@tanstack/react-table";
import clsx from "clsx";
import { Reorder, useDragControls } from "motion/react";
import { Children, memo } from "react";
import { Icon } from "../../icons";
import { Tooltip } from "../../tooltip";
import type { ChildrenProps } from "../../types";
import { ColumnOptions } from "./head/column-options";
import type { Header } from "./types";
import { getCommonPinningStyles } from "./utils";

/** Props Interface */
interface DataTableThProps<T extends RowData> extends ChildrenProps {
	header: Header<T, unknown>;
}

function DataTableThComponent<T extends RowData>({ children, header }: DataTableThProps<T>) {
	/** Computed */
	const isPinned = header.column.getIsPinned();

	const dragControls = useDragControls();
	const meta = header.column.columnDef.meta;

	const isStringTitle = Children.toArray(children).every((item) => typeof item === "string");

	/** Render */
	return (
		<Reorder.Item
			value={header}
			key={header.id}
			className={clsx("table-th", isPinned && "z-1!")}
			data-pinned-left={isPinned === "start" ? true : undefined}
			data-pinned-right={isPinned === "end" ? true : undefined}
			onKeyUp={() => {}}
			onDoubleClick={() => {
				header.column.toggleSorting();
			}}
			dragControls={dragControls}
			dragMomentum={false}
			dragElastic={false}
			as="div"
			dragListener={false}
			style={getCommonPinningStyles(header.column)}
			layout="position"
			transition={{ duration: 0, ease: "linear" }}
		>
			<Tooltip
				label={isStringTitle ? children : ""}
				openDelay={1000}
				isDisabled={!isStringTitle}
			>
				<div className={clsx("flex items-center gap-2")}>
					<div
						className={clsx("grid justify-between items-center w-full")}
						style={{ gridTemplateColumns: "auto max-content" }}
					>
						<div className="h-stack shrink overflow-x-hidden">
							{!isPinned && (
								<div
									className="cursor-grab active:cursor-grabbing reorder-handle -mx-1 opacity-30"
									onPointerDown={(e) => dragControls.start(e)}
								>
									<Icon name="GripVertical" />
								</div>
							)}
							{header.column.getIsFiltered() && (
								<Icon
									name="Search"
									className="stroke-accent-500"
								/>
							)}

							<div
								className="line-clamp-1"
								style={{
									textOverflow: "ellipsis",
								}}
							>
								{children}
							</div>
						</div>
						{!meta?.hideOptions && <ColumnOptions header={header} />}
					</div>
				</div>
			</Tooltip>
		</Reorder.Item>
	);
}

export const DataTableTh = memo(DataTableThComponent) as typeof DataTableThComponent;
